import * as Sentry from '@sentry/react-router'

/**
 * A tab left open across a deploy asks this build for the previous build's hashed chunks, which no
 * longer exist. Recovering means one hard reload onto the current build. #25536
 */
export function installStaleBuildRecovery() {
  window.addEventListener('vite:preloadError', (event) => {
    const preloadError = event as VitePreloadErrorEvent
    const recovery = recoverFromStaleBuild({ error: preloadError.payload, source: 'vite:preloadError' })
    // Vite rethrows unless the event is cancelled. Cancel it only when a reload is coming, so that a
    // withheld reload still surfaces the failure to the error boundary rather than hanging Suspense.
    if (recovery === 'reload') preloadError.preventDefault()
  })

  window.addEventListener('unhandledrejection', (event) => {
    if (isModuleLoadError(event.reason)) {
      recoverFromStaleBuild({ error: event.reason, source: 'unhandledrejection' })
    }
  })
}

export function isModuleLoadError(error: unknown) {
  const message = getErrorMessage(error)
  return MODULE_LOAD_ERROR_MESSAGES.some((candidate) => message.includes(candidate))
}

/**
 * Returns what it did, so a caller rendering an error boundary can keep showing the error page when
 * no reload is coming.
 */
export function recoverFromStaleBuild({ error, source }: { error: unknown; source: string }): StaleBuildRecovery {
  const recovery = decideStaleBuildRecovery({
    reloadAlreadyRequested: hasRequestedReloadThisPage,
    mutationInFlight: isMutationInFlight,
    previousReloadAtEpochMs: readPreviousReloadAtEpochMs(),
    nowEpochMs: Date.now()
  })

  if (recovery === 'reload') {
    hasRequestedReloadThisPage = true
    writePreviousReloadAtEpochMs(Date.now())
    window.location.reload()
  }

  if (recovery === 'reload-did-not-recover' || recovery === 'mutation-in-flight') {
    // Reported as a fresh Error rather than the original: Chrome's wording for a failed dynamic
    // import starts "Failed to fetch dynamically imported module", which the client's beforeSend
    // drops as network noise, so the original would silently never arrive.
    Sentry.captureException(new Error(`Stale build not recovered: ${recovery}`), {
      fingerprint: [`stale-build-${recovery}`],
      extra: { source, originalMessage: getErrorMessage(error) }
    })
  }

  return recovery
}

/**
 * True from the moment a reload is requested until the new document replaces this one. React keeps
 * rendering in that window, and what it renders is misleading: cancelling `vite:preloadError` stops
 * Vite rethrowing, so the import resolves `undefined` and `React.lazy` throws "Cannot read
 * properties of undefined (reading 'default')" — a null-safety bug that does not exist. Nothing
 * raised in this window describes anything a reader could act on.
 */
export function isReloadingForStaleBuild() {
  return hasRequestedReloadThisPage
}

/** Lets a reload be withheld while a submission the rider cannot afford to lose is in flight. */
export function setMutationInFlight(inFlight: boolean) {
  isMutationInFlight = inFlight
}

export type StaleBuildRecovery = 'reload' | 'reload-already-requested' | 'mutation-in-flight' | 'reload-did-not-recover'

export function decideStaleBuildRecovery({
  reloadAlreadyRequested,
  mutationInFlight,
  previousReloadAtEpochMs,
  nowEpochMs
}: {
  reloadAlreadyRequested: boolean
  mutationInFlight: boolean
  previousReloadAtEpochMs: number | null
  nowEpochMs: number
}): StaleBuildRecovery {
  if (reloadAlreadyRequested) return 'reload-already-requested'
  // nginx runs `proxy_ignore_client_abort on`, so a reload here does not cancel the write — it only
  // hides the outcome from the rider, who then submits a second time. #25727
  if (mutationInFlight) return 'mutation-in-flight'
  if (previousReloadAtEpochMs !== null && nowEpochMs - previousReloadAtEpochMs < RELOAD_COOLDOWN_MS) {
    return 'reload-did-not-recover'
  }
  return 'reload'
}

/**
 * A reload that lands on the current build fixes the missing chunk, so the same error arriving again
 * moments later means it did not land — reloading again would loop. Hours later it is a second
 * deploy and a fresh reload is the right answer, which is why the guard expires rather than latching.
 */
const RELOAD_COOLDOWN_MS = 60_000

const PREVIOUS_RELOAD_STORAGE_KEY = 'staleBuildReloadAtEpochMs'

const MODULE_LOAD_ERROR_MESSAGES = [
  'Importing a module script failed', // Safari
  'Failed to fetch dynamically imported module', // Chrome, Edge
  'error loading dynamically imported module', // Firefox
  'Unable to preload CSS for' // Vite's preload helper, every browser
]

/**
 * Lives only as long as the current page, so that sibling imports failing in the same tick do not
 * each queue their own reload. Across a reload the stored timestamp is what guards.
 */
let hasRequestedReloadThisPage = false

let isMutationInFlight = false

/** Absent, unparseable and unreadable storage all mean the same thing: no reload has been recorded. */
function readPreviousReloadAtEpochMs(): number | null {
  try {
    const stored = Number(window.sessionStorage.getItem(PREVIOUS_RELOAD_STORAGE_KEY))
    return Number.isFinite(stored) && stored > 0 ? stored : null
  } catch {
    return null
  }
}

function writePreviousReloadAtEpochMs(epochMs: number) {
  try {
    window.sessionStorage.setItem(PREVIOUS_RELOAD_STORAGE_KEY, String(epochMs))
  } catch {
    // Storage is unavailable, so the cooldown cannot be enforced across the reload. Reloading
    // unguarded still beats leaving the rider on a dead page.
  }
}

function getErrorMessage(error: unknown) {
  if (error instanceof Error) return error.message
  return typeof error === 'string' ? error : ''
}

interface VitePreloadErrorEvent extends Event {
  payload: unknown
}
